import React, {useEffect, useState} from 'react';
import {
    parseISO,
    parse,
    addDays,
    isSameDay,
    isBefore,
    startOfMonth,
    differenceInDays,
    isWithinInterval,
    format,
    addMonths,
    getDaysInMonth,
    endOfMonth,
    startOfDay,
    endOfDay,
    isAfter,
    differenceInCalendarDays,
    isLastDayOfMonth
} from 'date-fns';
import {uk} from 'date-fns/locale';
import {
    Popper, IconButton, Paper, Stack, Tooltip, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, Divider,
    TextField, MenuItem
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {useFetcher, useNavigate} from 'react-router-dom';
import AuthProvider from '../hooks/authProvider';
import jsonToFormData from '../hooks/jsonToFormData';
import {showSnackbar} from "./snackbarUtils";
import api from "../hooks/useApi";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";

// Helper: compare YYYY-MM-DD strings inclusively (module scope & hoisted)
function isBetweenYMD(d, s, e) {
    return !!d && !!s && !!e && (d >= s && d <= e);
}

function kFormatter(num) {
    return Math.abs(num) > 999 ? Math.sign(num) * ((Math.abs(num) / 1000).toFixed(1)) + 'k' : (Math.sign(num) * Math.abs(num)).toFixed(0)
}

// --- robust datetime parser: supports "2025-08-23T21:00:00+03:00" and "2025-08-23 21:00:00.000000" ---
const parseDateTime = (s) => {
    if (!s) return new Date(NaN);
    if (s instanceof Date) return s;
    const str = String(s).trim();

    // ✅ поддержка строк вида "2025-09-01"
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return parse(str, 'yyyy-MM-dd', new Date());
    }

    if (str.includes('T')) return parseISO(str);
    const fmt = str.includes('.') ? 'yyyy-MM-dd HH:mm:ss.SSSSSS' : 'yyyy-MM-dd HH:mm:ss';
    return parse(str, fmt, new Date());
};

const getPriceForDate = (date, priceList) => {
    const prices = Array.isArray(priceList) ? priceList : [priceList];
    const reversedPriceList = [...prices].reverse();

    const priceForDate = reversedPriceList.find(({period_start, period_end}) => {
        const startDate = parseISO(period_start);
        const endDate = addDays(parseISO(period_end), 1);
        return isWithinInterval(date, {start: startDate, end: endDate});
    });

    return priceForDate ? priceForDate.price : null;
};

// Function to generate an array of dates between two dates using date-fns
const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let currentDate = startDate;
    while (isBefore(currentDate, endDate) || isSameDay(currentDate, endDate)) {
        dates.push(currentDate);
        currentDate = addDays(currentDate, 1);
    }
    return dates;
};

// eslint-disable-next-line no-unused-vars
const groupBy = (arr, keyFn) => {
    const map = {};
    arr.forEach((item) => {
        const k = keyFn(item);
        if (!map[k]) map[k] = [];
        map[k].push(item);
    });
    return Object.values(map);
};

const getBookingStart = (b) => startOfDay(parseDateTime(b.booking_from));
const getBookingEndExclusive = (b) =>
    //addDays(startOfDay(parseDateTime(b.booking_to)), 1);
    startOfDay(parseDateTime(b.booking_to));

export const isActiveVisualBooking = (booking) => {
    const status = booking?.status != null ? String(booking.status) : '';
    const source = booking?.booking_system_from != null ? String(booking.booking_system_from) : '';
    return status !== '2' && status !== '6' && source !== '4';
};

const RenderTableRows = ({
                             data,
                             allDates,
                             onBookingClick,
                             setAnchorEl,
                             setPopupMode,
                             setClickedBookingId,
                             setOverListItems,
                             setOverListOpen
                         }) => {
    let overallRowIndex = 0;
    const mod_data = flattenObjects(data);

    return mod_data.map((obj, rowIndex) => {
        overallRowIndex++;
        if (obj.children_obj?.length > 0) {
            return (
                <tr key={overallRowIndex} style={{backgroundColor: '#EEEEEE'}}>
                    <td style={{textAlign: 'start'}}>
                        {obj.name}
                    </td>
                    <td colSpan={1 + Math.abs(differenceInDays(allDates[0], allDates[allDates.length - 1]))}/>
                </tr>
            );
        }

        return (
            <React.Fragment key={overallRowIndex}>
                <tr>
                    <td
                        className='object_title'
                        data-x={overallRowIndex}
                        data-pricesindex={rowIndex}
                        style={{position: 'sticky', paddingLeft: '16px', backgroundColor: "#ee97f8"}}
                    >
                        {obj.title || obj.name}
                    </td>

                    {allDates.map((date, index) => {
                        const overlapping = obj.bookings?.filter((booking) => {
                            const s = getBookingStart(booking);
                            const eExcl = getBookingEndExclusive(booking);
                            return !isBefore(date, s) && isBefore(date, eExcl);
                        }) || [];

                        const toStr = (v) => (v != null ? String(v) : '');
                        const isOver = (b) => toStr(b.status) === '6' || toStr(b.booking_system_from) === '4';
                        const isExternal = (b) => toStr(b.booking_system_from) === '2' || toStr(b.booking_system_from) === '3';
                        const isReal = isActiveVisualBooking;
                        const realBooking = overlapping.find(isReal) || null;

                        if (!realBooking) {
                            const isEndMonth = isLastDayOfMonth(date);
                            const className = ['selectable'];
                            if (isEndMonth) className.push('end_of_month');

                            return (
                                <td
                                    key={index}
                                    className={className.join(' ')}
                                    data-objid={obj.id}
                                    data-type={obj.ctype}
                                    data-date={format(date, 'yyyy-MM-dd')}
                                    data-x={overallRowIndex}
                                    data-y={index}
                                />
                            );
                        }

                        const s = getBookingStart(realBooking);
                        const eExcl = getBookingEndExclusive(realBooking);
                        const lastOcc = addDays(eExcl, -1);
                        const firstDate = allDates[0];
                        const lastDate = allDates[allDates.length - 1];

                        const isFromBeforeFirst = isBefore(s, firstDate);
                        const isFromAfterFirst = !isFromBeforeFirst;
                        const isToAfterLast = isAfter(lastOcc, lastDate);
                        const isToBeforeLast = !isToAfterLast;

                        let className;

                        if (realBooking.booking_system_from === '0') {
                            className = [
                                `calendarColor${realBooking.booking_system_from}`,
                            ];
                        } else {
                            className = [
                                `calendarColor${realBooking.booking_system_from}`,
                            ];
                        }

                        const vStart = isFromBeforeFirst ? firstDate : s;
                        const vLastOcc = isToAfterLast ? lastDate : lastOcc;
                        let colSpan = Math.max(0, differenceInCalendarDays(vLastOcc, vStart) + 1);

                        if (isFromBeforeFirst && isToAfterLast) {
                        } else if (isFromBeforeFirst && isToBeforeLast) {
                        } else if (isFromAfterFirst && isToAfterLast) {
                        }

                        const isBookingStartDate = isSameDay(date, vStart);

                        if (isBookingStartDate) {
                            const realList = (obj.bookings || []).filter(isActiveVisualBooking);
                            const hasPrevTouch = realList.some(b =>
                                isSameDay(getBookingEndExclusive(b), s)
                            );

                            const hasNextTouch = realList.some(b =>
                                isSameDay(getBookingStart(b), eExcl)
                            );

                            const clipPath = (() => {
                                const leftCut = !hasPrevTouch;
                                const rightCut = !hasNextTouch;
                                if (leftCut && rightCut) return 'polygon(12px 0, 100% 0, calc(100% - 15px) 100%, 0 100%)';
                                if (!leftCut && rightCut) return 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)';
                                if (leftCut && !rightCut) return 'polygon(12px 0, 100% 0, 100% 100%, 0 100%)';
                                return 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
                            })();

                            return (
                                <td
                                    key={index}
                                    data-x={overallRowIndex}
                                    data-y={index}
                                    colSpan={colSpan}
                                    data-type={obj.ctype}
                                    data-bookid={realBooking.id}
                                    title={`Час заїзду: ${realBooking.time_arrived?.replace('-', ':') ?? ''}`}
                                    className={className.join(' ')}
                                    onClick={(ev) =>
                                        onBookingClick && onBookingClick(
                                            ev,
                                            realBooking.id,
                                            isExternal(realBooking) ? 'booking-external' : 'booking'
                                        )
                                    }
                                >
                                    <div
                                        className={className.join(' ')}
                                        style={{
                                            clipPath,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            height: '40px',
                                            padding: '0 6px',
                                            boxSizing: 'border-box',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        <Typography component="span" variant="body2" noWrap>
                                            {`${realBooking.first_name} ${realBooking.last_name}`}
                                        </Typography>
                                    </div>
                                </td>
                            );
                        }

                        return null;
                    })}
                </tr>

                {(() => {
                    const toStr = (v) => (v != null ? String(v) : '');
                    const isOver = (b) => toStr(b.status) === '6' || toStr(b.booking_system_from) === '4';

                    const clampIndex = (d) => {
                        if (isBefore(d, allDates[0])) return 0;
                        if (isAfter(d, allDates[allDates.length - 1])) return allDates.length - 1;
                        const idx = allDates.findIndex((x) => isSameDay(x, d));
                        return idx === -1 ? 0 : idx;
                    };

                    const groupsMap = {};
                    (obj.bookings || []).filter(isOver).forEach((b) => {
                        const s = getBookingStart(b);
                        const eExcl = getBookingEndExclusive(b);
                        const lastOcc = addDays(eExcl, -1);
                        const first = allDates[0];
                        const last = allDates[allDates.length - 1];
                        const intersectsVisibleWindow =
                            isBefore(s, addDays(last, 1)) && isAfter(eExcl, first);

                        if (!intersectsVisibleWindow) return;

                        const vStart = isBefore(s, first) ? first : s;
                        const vLastOcc = isAfter(lastOcc, last) ? last : lastOcc;
                        const si = clampIndex(vStart);
                        const ei = clampIndex(vLastOcc);
                        const key = `${si}_${ei}`;

                        if (!groupsMap[key]) groupsMap[key] = {s, e: vLastOcc, _start: si, _end: ei, items: []};
                        groupsMap[key].items.push(b);
                    });

                    const segments = Object.values(groupsMap)
                        .sort((a, b) => (a._start - b._start) || (a._end - b._end));

                    const lanes = [];
                    const laneEnds = [];

                    segments.forEach((seg) => {
                        let placed = false;
                        for (let i = 0; i < lanes.length; i++) {
                            if (seg._start > laneEnds[i]) {
                                lanes[i].push(seg);
                                laneEnds[i] = seg._end;
                                placed = true;
                                break;
                            }
                        }
                        if (!placed) {
                            lanes.push([seg]);
                            laneEnds.push(seg._end);
                        }
                    });

                    return lanes.length === 0 ? (
                        <tr className="overbooking-row">
                            <td style={{height: 16, padding: 0}}/>
                            {allDates.map((_, i) => (
                                <td key={i} style={{height: 16, padding: 0}}/>
                            ))}
                        </tr>
                    ) : (
                        lanes.map((lane, li) => (
                            <tr key={`overlane_${li}`} className="overbooking-row">
                                <td style={{height: 16, padding: 0}}/>
                                {(() => {
                                    const cells = [];
                                    let cursor = 0;

                                    lane.forEach((seg, si) => {
                                        while (cursor < seg._start) {
                                            cells.push(
                                                <td key={`g_${li}_${cursor}`} style={{height: 16, padding: 0}}/>
                                            );
                                            cursor++;
                                        }

                                        const span = (seg._end - seg._start + 1);
                                        const firstItem = seg.items[0];
                                        const isMany = seg.items.length > 1;

                                        cells.push(
                                            <td
                                                key={`s_${li}_${si}`}
                                                data-x={overallRowIndex}
                                                data-y={seg._start}
                                                colSpan={span}
                                                data-type={obj.ctype}
                                                style={{position: 'relative', padding: 0, height: 16}}
                                                onClick={(ev) => {
                                                    if (isMany) {
                                                        setOverListItems(seg.items);
                                                        setOverListOpen(true);
                                                    } else {
                                                        setAnchorEl(ev.currentTarget);
                                                        setPopupMode('booking-over');
                                                        setClickedBookingId(firstItem.id);
                                                    }
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: '#009e3d',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '0 6px',
                                                        boxSizing: 'border-box',
                                                        position: 'relative',
                                                    }}
                                                    title="Овербукінг"
                                                >
                                                    <Typography component="span" variant="caption" style={{
                                                        color: 'white',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {isMany
                                                            ? `Овербукінг — ${seg.items.length} шт`
                                                            : `Овербукінг — ${(firstItem.first_name || '')} ${(firstItem.last_name || '')}`.trim()}
                                                    </Typography>
                                                </div>
                                            </td>
                                        );

                                        cursor = seg._end + 1;
                                    });

                                    while (cursor < allDates.length) {
                                        cells.push(<td key={`t_${li}_${cursor}`} style={{height: 16, padding: 0}}/>);
                                        cursor++;
                                    }

                                    return cells;
                                })()}
                            </tr>
                        ))
                    );
                })()}

                {obj.children_obj?.length > 0 && (
                    <tr>
                        <td
                            style={{textAlign: 'start'}}
                            colSpan={Math.abs(differenceInDays(allDates[0], allDates[allDates.length - 1])) + 1}
                        >
                            Дочірні об'єкти
                        </td>
                    </tr>
                )}

                {obj.rates && obj.rates.length > 0 && obj.rates.map((rate, rateIndex) => (
                    rate.prices && rate.prices.length > 0 ? (
                        <tr key={`prices_${rateIndex}`} className={`prices_${rateIndex}`}>
                            {rate.base_rate_name === 'base' ? (
                                <td
                                    data-x={overallRowIndex++}
                                    style={{textAlign: 'right', backgroundColor: "#16bee5"}}
                                >
                                    {rate.name}, ₴
                                </td>
                            ) : (
                                <td
                                    data-x={overallRowIndex++}
                                    style={{textAlign: 'right', backgroundColor: "#dabe58", padding: "5px"}}
                                >
                                    {rate.name}, ₴
                                    {rate.base_rate_name !== 'base' && (
                                        <span style={{display: "block", fontSize: "13px", color: "#555"}}>
                                            залежить від {rate.base_rate_name}
                                        </span>
                                    )}
                                </td>
                            )}

                            {(() => {
                                const prices = Array.isArray(rate.prices) ? rate.prices : [];
                                const pausePeriods = Array.isArray(rate.pause_periods)
                                    ? rate.pause_periods
                                        .filter(period => period && period.start && period.end)
                                        .map(period => ({
                                            start: String(period.start).slice(0, 10),
                                            end: String(period.end).slice(0, 10),
                                        }))
                                    : [];

                                console.log('🔍 Rate debug:', {
                                    rate_id: rate.id,
                                    rate_name: rate.name,
                                    prices_count: prices.length,
                                    first_items_sample: prices.slice(0, 3),
                                    pause_periods_count: pausePeriods.length,
                                    first_pause: pausePeriods[0] || null,
                                });

                                return allDates.map((date, index) => {
                                    const priceForDate = prices.find(price =>
                                        isWithinInterval(date, {
                                            start: parseISO(price.period_start),
                                            end: addDays(parseISO(price.period_end), 1)
                                        })
                                    );

                                    const dateStr = format(date, 'yyyy-MM-dd');
                                    const pausePeriod = pausePeriods.find(period => period.start && period.end && isBetweenYMD(dateStr, period.start, period.end));
                                    const pps = pausePeriod ? pausePeriod.start : '';
                                    const ppe = pausePeriod ? pausePeriod.end : '';
                                    const paused = Boolean(pausePeriod);

                                    if (index < 3 && rateIndex < 1) {
                                        console.log('🧪 cell-check', {
                                            rate_id: rate.id,
                                            dateStr,
                                            pps,
                                            ppe,
                                            paused,
                                            price_id: priceForDate && priceForDate.price_id
                                        });
                                    }

                                    const isEndMonth = isLastDayOfMonth(date);
                                    const className = ['selectable_price'];
                                    if (isEndMonth) className.push('end_of_month');

                                    if (rate.base_rate_name === 'base') {
                                        const cls = [...className];
                                        if (paused) cls.push('paused-price');
                                        return (
                                            <td
                                                className={cls.join(' ')}
                                                key={index}
                                                data-objid={obj.id}
                                                data-childrenid={rate.children_id}
                                                data-rateid={rate.id}
                                                data-priceid={priceForDate ? priceForDate.price_id : ''}
                                                data-type={obj.ctype}
                                                data-price={priceForDate ? priceForDate.clean_price : ''}
                                                data-tax={priceForDate ? priceForDate.tax : ''}
                                                data-taxunit={priceForDate ? priceForDate.another_amount_units : ''}
                                                data-pricetitle={priceForDate ? getPriceForDate(date, priceForDate) : 'empty'}
                                                title={priceForDate ? getPriceForDate(date, priceForDate) : ''}
                                                data-date={dateStr}
                                                data-pause-start={pps}
                                                data-pause-end={ppe}
                                                data-x={overallRowIndex}
                                                data-y={index}
                                                style={{
                                                    fontSize: "13px",
                                                    padding: "2px",
                                                    textAlign: "center",
                                                    fontWeight: 'bold',
                                                    backgroundColor: paused ? "#ffe6e6" : undefined,
                                                }}
                                            >
                                                {priceForDate ? kFormatter(getPriceForDate(date, priceForDate)) : null}
                                            </td>
                                        );
                                    } else {
                                        const cls = [...className];
                                        if (paused) cls.push('paused-price');
                                        return (
                                            <td
                                                className={cls.join(' ')}
                                                key={index}
                                                data-objid={obj.id}
                                                data-childrenid={rate.children_id}
                                                data-rateid={rate.id}
                                                data-priceid={priceForDate ? priceForDate.price_id : ''}
                                                data-type={obj.ctype}
                                                data-price={priceForDate ? priceForDate.clean_price : ''}
                                                data-tax={priceForDate ? priceForDate.tax : ''}
                                                data-taxunit={priceForDate ? priceForDate.another_amount_units : ''}
                                                data-pricetitle={priceForDate ? getPriceForDate(date, priceForDate) : 'empty'}
                                                title={priceForDate ? getPriceForDate(date, priceForDate) : ''}
                                                data-date={dateStr}
                                                data-pause-start={pps}
                                                data-pause-end={ppe}
                                                data-x={overallRowIndex}
                                                data-y={index}
                                                style={{
                                                    fontSize: "13px",
                                                    padding: "2px",
                                                    textAlign: "center",
                                                    fontWeight: 'bold',
                                                    backgroundColor: paused ? "#ffe6e6" : "#f2f2f2"
                                                }}
                                            >
                                                {priceForDate ? kFormatter(getPriceForDate(date, priceForDate)) : null}
                                            </td>
                                        );
                                    }
                                });
                            })()}
                        </tr>
                    ) : (
                        <tr key={`prices_${rateIndex}`} className={`prices_${rateIndex}`}>
                            <td
                                colSpan={allDates.length + 1}
                                style={{textAlign: 'center', backgroundColor: "#f8f9fa", color: "#6c757d"}}
                            >
                                {rate.name} – Немає даних про ціни
                            </td>
                        </tr>
                    )
                ))}
            </React.Fragment>
        );
    });
};

/**
 * A calendar table for single object
 */
const CalendarTableSingle = ({data, startDate, endDate}) => {
    const displayedStartDate = startDate;
    const displayedEndDate = endDate;

    const allDates = getDatesBetween(displayedStartDate, displayedEndDate);

    useEffect(() => {
        const STYLE_ID = 'paused-price-style';
        if (!document.getElementById(STYLE_ID)) {
            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = `.paused-price{ background-color:#ffe6e6 !important; }`;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        try {
            console.log('💰 Calendar data payload:', data);
        } catch (_) {
        }
    }, [data]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [popupMode, setPopupMode] = useState(null);
    const [clickedBookingId, setClickedBookingId] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);

    const [closeModalOpen, setCloseModalOpen] = useState(false);
    const [closeFormValues, setCloseFormValues] = useState(null);

    const currentHourDash = format(new Date(), 'HH') + '-00';

    const [overListOpen, setOverListOpen] = useState(false);
    const [overListItems, setOverListItems] = useState([]);

    const [confirmOverOpen, setConfirmOverOpen] = useState(false);

    const [confirmBackOpen, setConfirmBackOpen] = useState(false);
    const [targetOverId, setTargetOverId] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [editValues, setEditValues] = useState(null);
    const [editBookingId, setEditBookingId] = useState(null);

    const allowedEditFields = ['booking_from', 'booking_to', 'first_name', 'last_name', 'email', 'phone', 'comments', 'time_arrived'];

    const handleBookedCellClick = (event, bookingId, mode = 'booking') => {
        setAnchorEl(event.currentTarget);
        setClickedBookingId(bookingId);
        setPopupMode(mode);
    };

    const norm = (v) => (v === null || v === undefined ? '' : v);
    const isoToLocalInput = (iso) => {
        if (!iso) return '';
        const d = parseDateTime(iso);
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const openEditModal = async (bookingId) => {
        if (!bookingId) return;
        try {
            setEditLoading(true);
            setEditBookingId(bookingId);
            const res = await api.get(`api/booking/child/${bookingId}/0/`);
            if (res && res.status === 200 && res.data && res.data.form) {
                const schema = res.data.form;
                const filteredSchema = Object.fromEntries(
                    Object.entries(schema).filter(([k]) => allowedEditFields.includes(k))
                );
                setEditForm(filteredSchema);

                const values = {};
                allowedEditFields.forEach((k) => {
                    if (!filteredSchema[k]) return;
                    const field = filteredSchema[k];
                    if (field.type === 'DateTimeInput') {
                        values[k] = isoToLocalInput(field.initial);
                    } else {
                        values[k] = norm(field.initial);
                    }
                });

                setEditValues(values);
                setEditOpen(true);
                setAnchorEl(null);
            } else {
                showSnackbar('Не вдалося отримати форму редагування', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            showSnackbar(`Помилка отримання форми редагування: ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditChange = (key, value) => setEditValues((prev) => ({...prev, [key]: value}));

    const handleEditSubmit = async () => {
        if (!editBookingId || !editValues) return;
        try {
            const formData = jsonToFormData(editValues);
            const response = await api.patch(`api/booking/child/${editBookingId}/0/`, formData);
            if (response.status === 200) {
                showSnackbar('Бронювання успішно оновлено!', 'success', {
                    sx: {
                        backgroundColor: '#1de416',
                        color: 'white'
                    }
                });
                setEditOpen(false);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                showSnackbar('Помилка при оновлені бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            if (e.response && e.response.status === 409) {
                showSnackbar('На обрані дати вже є бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            } else {
                showSnackbar(`Помилка при оновлені бронювання ${e}`, 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        }
    };

    const openBookingDetails = async (id) => {
        if (!id) return;
        try {
            setDetailsLoading(true);
            const response = await api.get(`/api/booking_modules/use/booking/admin_book/${id}/`);
            if (response?.status === 200) {
                if (response.data?.data) {
                    setBookingDetails(response.data.data);
                    setDetailsOpen(true);
                    setAnchorEl(null);
                }
            } else {
                showSnackbar('Не вдалося отримати деталі бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            showSnackbar(`Помилка отримання деталей: ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRemoveOverbooking = (id) => {
        if (!id) return;
        setTargetOverId(id);
        setConfirmBackOpen(true);
    };

    const isBookingICS = (booking) => {
        return (
            booking?.email === 'booking-calendar' &&
            booking?.phone === 'booking-ics'
        );
    };

    const isClosedPeriod = (booking) => {
        return String(booking?.first_name || '')
            .toLowerCase()
            .includes('період закрито');
    };

    const doRemoveOverbooking = async () => {
        if (!targetOverId) return;

        try {
            let details = null;

            try {
                const detailsRes = await api.get(`/api/booking_modules/use/booking/admin_book/${targetOverId}/`);
                if (detailsRes?.status === 200 && detailsRes?.data?.data) {
                    details = detailsRes.data.data;
                }
            } catch (e) {
                console.warn('Не вдалося отримати деталі бронювання перед зміною статусу', e);
            }

            let url = `/api/booking_modules/use/booking/update-status/${targetOverId}/4/1/`;

            if (isClosedPeriod(details)) {
                url = `/api/booking_modules/use/booking/update-status/${targetOverId}/4/0/`;
            } else if (isBookingICS(details)) {
                url = `/api/booking_modules/use/booking/update-status/${targetOverId}/4/3/`;
            }

            const source = isClosedPeriod(details) ? 0 : isBookingICS(details) ? 3 : 1;
            const res = await api.patch(url, {status: 4, statusBook: source});
            if (res.status < 200 || res.status >= 300) throw new Error(`HTTP ${res.status}`);
            showSnackbar('Статус оновлено', 'success', {sx: {backgroundColor: '#1de416', color: 'white'}});
            setConfirmBackOpen(false);
            setOverListOpen(false);
            setAnchorEl(null);
            setTargetOverId(null);
            setTimeout(() => window.location.reload(), 2000);
        } catch (e) {
            showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        }
    };

    const open = Boolean(anchorEl);
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();
    const fetcher = useFetcher();

    const handleClosePeriod = () => {
        openClosePeriodModal();
    };

    const openClosePeriodModal = () => {
        const els = document.querySelectorAll('.selected');
        if (!els || els.length === 0) {
            alert('Не обрано період.');
            return;
        }
        const startPeriod = els[0].dataset.date;
        const endPeriod = els[els.length - 1].dataset.date;
        const hotelType = els[0].dataset.type;
        const obj_id = els[0].dataset.objid;

        const defaults = {
            booking_from: startPeriod,
            booking_to: endPeriod,
            email: 'internal_close@bit-ua.com',
            first_name: 'Період закрито.',
            last_name: `Ким: (${AuthProvider.user?.username || 'менеджер'})`,
            phone: '+380000000000',
            status: '4',
            default_hotel_children: obj_id,
            check_info: true,
            time_arrived: currentHourDash,
            booking_system_from: '0'
        };
        defaults[hotelType] = obj_id;

        setCloseFormValues(defaults);
        setCloseModalOpen(true);
    };

    const submitCloseForm = async () => {
        if (!closeFormValues) return;

        try {
            const formData = jsonToFormData(closeFormValues);
            const response = await api.post('/api/BookingFormInit/', formData);

            if (response && response.status >= 200 && response.status < 300) {
                setCloseModalOpen(false);
                showSnackbar("Період успішно закрито!", "success", {
                    sx: {backgroundColor: "#1de416", color: "white"}
                });
                setTimeout(() => window.location.reload(), 2000);
            } else {
                throw new Error(`HTTP ${response?.status}`);
            }
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data?.message;
            if (status === 409) {
                setCloseModalOpen(false);
                showSnackbar(`${data}`, "error", {
                    sx: {backgroundColor: "#ff0000", color: "white"}
                });
            } else {
                setCloseModalOpen(false);
                showSnackbar(`Виникла помилка при закритті періоду. ${error}`, "error", {
                    sx: {backgroundColor: "#ff0000", color: "white"}
                });
            }
        }
    };

    useEffect(() => {
        const table = document.querySelector('table');
        if (!table) return;

        let selecting = false;
        let startCell, endCell;

        const handleMouseDown = (ev) => {
            const targetCell = ev.target.closest('td.selectable, td.selectable_price');
            if (targetCell) {
                selecting = true;
                startCell = targetCell;
                endCell = targetCell;
                colorAllCells();
            }
        };

        const handleMouseMove = (ev) => {
            if (selecting) {
                const targetCell = ev.target.closest('td.selectable, td.selectable_price');
                if (targetCell) {
                    endCell = targetCell;
                    colorAllCells();
                }
            }
        };

        const handleMouseUp = (ev) => {
            const isSelectable = ev.target.classList.contains('selectable');
            const isSelectablePrice = ev.target.classList.contains('selectable_price');
            if (isSelectable || isSelectablePrice) {
                setAnchorEl(ev.target);
                setClickedBookingId(null);
                setPopupMode(isSelectablePrice ? 'price' : 'book');
            }
            selecting = false;
        };

        const colorAllCells = () => {
            const cells = table.querySelectorAll('td.selectable, td.selectable_price');
            const [, startY] = getCellIndices(startCell);
            const [endX, endY] = getCellIndices(endCell);

            cells.forEach(cell => {
                const [cellX, cellY] = getCellIndices(cell);
                const withinRangeX = endX === cellX;
                const withinRangeY = (startY <= cellY && cellY <= endY) || (endY <= cellY && cellY <= startY);

                if (withinRangeX && withinRangeY) {
                    cell.classList.add('selected');
                } else {
                    cell.classList.remove('selected');
                }
            });
        };

        const getCellIndices = (cell) => {
            return [parseInt(cell.dataset.x), parseInt(cell.dataset.y)];
        };

        const handleTouchStart = (ev) => {
            const targetCell = ev.target.closest('td.selectable, td.selectable_price');
            if (targetCell) {
                selecting = true;
                startCell = targetCell;
                endCell = targetCell;
                colorAllCells();
                ev.preventDefault();
            }
        };

        const handleTouchMove = (ev) => {
            if (selecting) {
                const touch = ev.touches[0];
                const deltaX = Math.abs(touch.clientX - startCell.getBoundingClientRect().left);
                const deltaY = Math.abs(touch.clientY - startCell.getBoundingClientRect().top);

                if (deltaX > deltaY) {
                    ev.preventDefault();
                    const targetCell = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('td.selectable, td.selectable_price');
                    if (targetCell) {
                        endCell = targetCell;
                        colorAllCells();
                    }
                }
            }
        };

        const handleTouchEnd = (ev) => {
            const isSelectable = ev.target.classList.contains('selectable');
            const isSelectablePrice = ev.target.classList.contains('selectable_price');
            if (isSelectable || isSelectablePrice) {
                setAnchorEl(ev.target);
                setClickedBookingId(null);
                setPopupMode(isSelectablePrice ? 'price' : 'book');
            }
            selecting = false;
        };

        table.addEventListener('mousedown', handleMouseDown);
        table.addEventListener('mousemove', handleMouseMove);
        table.addEventListener('mouseup', handleMouseUp);
        table.addEventListener('touchstart', handleTouchStart, {passive: false});
        table.addEventListener('touchmove', handleTouchMove, {passive: false});
        table.addEventListener('touchend', handleTouchEnd);

        return () => {
            table.removeEventListener('mousedown', handleMouseDown);
            table.removeEventListener('mousemove', handleMouseMove);
            table.removeEventListener('mouseup', handleMouseUp);
            table.removeEventListener('touchstart', handleTouchStart);
            table.removeEventListener('touchmove', handleTouchMove);
            table.removeEventListener('touchend', handleTouchEnd);
            setAnchorEl(null);
            startCell = null;
            endCell = null;
            table.querySelectorAll('td.selectable, td.selectable_price').forEach(cell => cell.classList.remove('selected'));
        };
    }, [data, displayedStartDate, displayedEndDate]);

    const renderTableHeader = () => {
        const monthNames = [];
        let currentMonth = displayedStartDate;
        const endDate = displayedEndDate;

        while (isBefore(currentMonth, endDate) || isSameDay(currentMonth, endDate)) {
            const monthName = format(currentMonth, 'LLL', {locale: uk});

            let remainingDays;
            if (isSameDay(currentMonth, startOfMonth(currentMonth))) {
                remainingDays = getDaysInMonth(currentMonth);
            } else {
                const lastDayOfMonth = endOfMonth(currentMonth);
                remainingDays = differenceInDays(lastDayOfMonth, currentMonth) + 1;
            }

            monthNames.push({
                name: monthName,
                days: remainingDays
            });

            currentMonth = addMonths(currentMonth, 1);
            currentMonth = startOfMonth(currentMonth);
        }

        return (
            <React.Fragment>
                <tr>
                    <th colSpan={30}>
                        <span>{`${format(startDate, 'dd MMMM', {locale: uk})} - ${format(endDate, 'dd MMMM yyyy', {locale: uk})}`}</span>
                    </th>
                </tr>
                <tr>
                    <th/>
                    {monthNames.map((el, index) => (
                        <th style={{textAlign: 'left'}} key={index} colSpan={el.days}>
                            {el.name}
                        </th>
                    ))}
                </tr>
                <tr>
                    <th/>
                    {allDates.map((date, index) => (
                        <th className='normal-th' key={index}>{format(date, 'dd')}</th>
                    ))}
                </tr>
            </React.Fragment>
        );
    };

    return (
        <div>
            <p style={{
                fontSize: "12px",
                color: "red",
                fontWeight: "bold",
                textAlign: "left",
                marginTop: "10px"
            }}>
                * Увага! Для змін цін залежних тарифів, необхідно змінювати ціну базового тарифу.
            </p>

            <div
                className={`${
                    (fetcher.state === 'loading' || fetcher.state === 'submitting') ? 'loading' : ''
                } desktop-version`}
                style={{display: 'block'}}
            >
                {fetcher.data?.error && (
                    <div style={{color: 'red'}}>
                        {fetcher.data.error}
                    </div>
                )}

                <div className="table-container" style={{width: '100%', overflow: 'scroll'}}>
                    <table className="desktop table-no_select" style={{width: 'fit-content'}}>
                        <thead>
                        {renderTableHeader()}
                        </thead>
                        <tbody>
                        <RenderTableRows
                            data={data}
                            allDates={allDates}
                            onBookingClick={handleBookedCellClick}
                            setAnchorEl={setAnchorEl}
                            setPopupMode={setPopupMode}
                            setClickedBookingId={setClickedBookingId}
                            setOverListItems={setOverListItems}
                            setOverListOpen={setOverListOpen}
                        />
                        </tbody>
                    </table>

                    <div className="spacer" style={{height: '30px'}}/>

                    <Popper
                        id="popover_buttons_menu"
                        open={open}
                        anchorEl={anchorEl || null}
                        placement="top"
                        onClose={handlePopoverClose}
                    >
                        <Paper>
                            <Stack direction="row">
                                {clickedBookingId ? (
                                    popupMode === 'booking-over' ? (
                                        <>
                                            <Tooltip title="Прибрати з овербукінгу" placement='top' arrow>
                                                <IconButton
                                                    aria-label="remove-overbooking"
                                                    onClick={() => handleRemoveOverbooking(clickedBookingId)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <UndoOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Переглянути деталі" placement='top' arrow>
                                                <IconButton
                                                    aria-label="details"
                                                    onClick={() => openBookingDetails(clickedBookingId)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <VisibilityOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : popupMode === 'booking-external' ? (
                                        <>
                                            <Tooltip title="Переглянути деталі" placement='top' arrow>
                                                <IconButton
                                                    aria-label="details"
                                                    onClick={() => openBookingDetails(clickedBookingId)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <VisibilityOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Ігнорувати це бронювання" placement='top' arrow>
                                                <IconButton onClick={async () => {
                                                    if (!clickedBookingId) return;
                                                    const ok = window.confirm('Ви збираєтесь ігнорувати це бронювання з зовнішньої системи.\nВи впевнені?');
                                                    if (!ok) return;
                                                    try {
                                                        const url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/6/4/`;
                                                        const res = await api.patch(url, {status: 6, statusBook: 4});
                                                        if (res && (res.status === 200 || res.status === 204)) {
                                                            showSnackbar("Статус оновлено", "success", {
                                                                sx: {
                                                                    backgroundColor: "#1de416",
                                                                    color: "white"
                                                                }
                                                            });
                                                            setAnchorEl(null);
                                                            setTimeout(async () => {
                                                                window.location.reload();
                                                            }, 2000);
                                                        } else {
                                                            showSnackbar('Помилка при оновлені статусу', 'error', {
                                                                sx: {
                                                                    backgroundColor: "#ff0000",
                                                                    color: "white"
                                                                }
                                                            });
                                                        }
                                                    } catch (e) {
                                                        showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                                                            sx: {
                                                                backgroundColor: "#ff0000",
                                                                color: "white"
                                                            }
                                                        });
                                                    }
                                                }}>
                                                    <AirplanemodeInactiveIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip title="Овербукінг" placement='top' arrow>
                                                <IconButton
                                                    aria-label="overbooking"
                                                    onClick={() => setConfirmOverOpen(true)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <LayersOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Відкрити період" placement='top' arrow>
                                                <IconButton
                                                    aria-label="open-period"
                                                    onClick={async () => {
                                                        if (!clickedBookingId) return;

                                                        const ok = window.confirm(
                                                            'Ви збираєтеся відкрити період (видалити запис закритого періоду).\nВи впевнені?'
                                                        );
                                                        if (!ok) return;

                                                        try {
                                                            const url = `/api/BookingFormInit/${clickedBookingId}/`;
                                                            const res = await api.delete(url);

                                                            if (res && res.status >= 200 && res.status < 300) {
                                                                showSnackbar('Період відкрито', 'success', {
                                                                    sx: {backgroundColor: '#1de416', color: 'white'},
                                                                });
                                                                setAnchorEl(null);
                                                                setTimeout(() => {
                                                                    window.location.reload();
                                                                }, 2000);
                                                            } else {
                                                                showSnackbar('Не вдалося відкрити період', 'error', {
                                                                    sx: {backgroundColor: '#ff0000', color: 'white'},
                                                                });
                                                            }
                                                        } catch (e) {
                                                            showSnackbar(`Помилка при відкритті періоду: ${e}`, 'error', {
                                                                sx: {backgroundColor: '#ff0000', color: 'white'},
                                                            });
                                                        }
                                                    }}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <DoDisturbOffIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Переглянути деталі" placement='top' arrow>
                                                <IconButton
                                                    aria-label="details"
                                                    onClick={() => openBookingDetails(clickedBookingId)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <VisibilityOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Редагувати" placement='top' arrow>
                                                <IconButton
                                                    aria-label="edit"
                                                    onClick={() => openEditModal(clickedBookingId)}
                                                    sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                                >
                                                    <EditOutlinedIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )
                                ) : popupMode === 'book' ? (
                                    <>
                                        <Tooltip title="Забронювати" placement="top" arrow>
                                            <IconButton
                                                aria-label="book"
                                                onClick={() => {
                                                    const els = document.querySelectorAll('.selected');
                                                    const start = els[0].dataset.date;
                                                    const end = els[els.length - 1].dataset.date;
                                                    const type = els[0].dataset.type;
                                                    const obj_id = els[0].dataset.objid;
                                                    const link = `book?book_from=${start}&book_to=${end}&${type}=${obj_id}`;
                                                    navigate(link);
                                                }}
                                                sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                            >
                                                <LockIcon/>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Закрити для бронювання" placement="top" arrow>
                                            <IconButton
                                                aria-label="close-period"
                                                onClick={() => {
                                                    handleClosePeriod();
                                                }}
                                                sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                            >
                                                <DoDisturbIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : popupMode === 'price' ? (
                                    <>
                                        <Tooltip title="Змінити ціну" placement="top" arrow>
                                            <IconButton
                                                aria-label="change-price"
                                                onClick={() => {
                                                    const els = document.querySelectorAll('.selected');
                                                    const start = els[0].dataset.date;
                                                    const end = els[els.length - 1].dataset.date;
                                                    const type = els[0].dataset.type;
                                                    const obj_id = els[0].dataset.objid;
                                                    const rate_id = els[0].dataset.rateid;
                                                    const children_id = els[0].dataset.childrenid;
                                                    let pricetitle = els[0].dataset.pricetitle;
                                                    let price_id, price, tax, tax_unit;

                                                    if (pricetitle !== undefined && pricetitle !== 'empty') {
                                                        price_id = els[0].dataset.priceid ?? '';
                                                        price = els[0].dataset.price ?? 0;
                                                        tax = els[0].dataset.tax ?? 0;
                                                        tax_unit = els[0].dataset.taxunit ?? "money";
                                                    } else {
                                                        price_id = 0;
                                                        price = 0;
                                                        tax = 0;
                                                        tax_unit = "money";
                                                        pricetitle = 'empty';
                                                    }

                                                    if (price_id === '') {
                                                        showSnackbar(`Для змін цін залежних тарифів, необхідно змінювати ціну базового тарифу.`, 'error', {
                                                            sx: {backgroundColor: "#ff0000", color: "white"}
                                                        });
                                                        return;
                                                    }

                                                    const link = `price?book_from=${start}&book_to=${end}&${type}=${obj_id}&rate_id=${rate_id}&pricetitle=${pricetitle}&price_id=${price_id}&price=${price}&tax=${tax}&tax_unit=${tax_unit}&children_id=${children_id}`;
                                                    navigate(link);
                                                }}
                                                sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                            >
                                                <AttachMoneyIcon/>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip
                                            title={(() => {
                                                try {
                                                    const els = document.querySelectorAll('.selected');
                                                    if (!els || els.length === 0) return 'Закрити тариф на обраний період';
                                                    const anyPaused = Array.from(els).some((el) => {
                                                        const dateStr = el.dataset.date;
                                                        const ps = el.dataset.pauseStart;
                                                        const pe = el.dataset.pauseEnd;
                                                        return el.classList.contains('paused-price') || (ps && pe && dateStr >= ps && dateStr <= pe);
                                                    });
                                                    return anyPaused ? 'Відкрити тариф на обраний період' : 'Закрити тариф на обраний період';
                                                } catch {
                                                    return 'Закрити тариф на обраний період';
                                                }
                                            })()}
                                            placement="top"
                                            arrow
                                        >
                                            <IconButton
                                                aria-label="toggle-price-pause"
                                                onClick={async () => {
                                                    try {
                                                        const els = document.querySelectorAll('.selected');
                                                        if (!els || els.length === 0) {
                                                            alert('Не обрано період.');
                                                            return;
                                                        }
                                                        const start = els[0].dataset.date;
                                                        const end = els[els.length - 1].dataset.date;
                                                        const rateId = els[0].dataset.rateid;
                                                        if (!rateId) {
                                                            showSnackbar('Не вдалося визначити тариф для цієї дії', 'error', {
                                                                sx: {backgroundColor: '#ff0000', color: 'white'}
                                                            });
                                                            return;
                                                        }

                                                        const anyPaused = Array.from(els).some((el) => {
                                                            const dateStr = el.dataset.date;
                                                            const ps = el.dataset.pauseStart;
                                                            const pe = el.dataset.pauseEnd;
                                                            return el.classList.contains('paused-price') || (ps && pe && dateStr >= ps && dateStr <= pe);
                                                        });

                                                        if (anyPaused) {
                                                            const pausedEl = Array.from(els).find((el) => {
                                                                const ps = el.dataset.pauseStart;
                                                                const pe = el.dataset.pauseEnd;
                                                                return ps && pe;
                                                            });
                                                            const pauseStart = pausedEl?.dataset.pauseStart;
                                                            const pauseEnd = pausedEl?.dataset.pauseEnd;
                                                            if (!pauseStart || !pauseEnd) {
                                                                showSnackbar('Не вдалося визначити межі паузи для тарифу', 'error', {
                                                                    sx: {backgroundColor: '#ff0000', color: 'white'}
                                                                });
                                                                return;
                                                            }

                                                            const res = await api.patch(`/api/rates/pause/unpause/${rateId}/`, {}, {
                                                                params: {
                                                                    pause_period_start: pauseStart,
                                                                    pause_period_end: pauseEnd,
                                                                },
                                                            });

                                                            if (res && res.status === 200) {
                                                                const selectedEls = document.querySelectorAll('.selected');
                                                                selectedEls.forEach((el) => {
                                                                    el.classList.remove('paused-price');
                                                                    el.removeAttribute('data-pause-start');
                                                                    el.removeAttribute('data-pause-end');
                                                                });
                                                                showSnackbar('Тариф відкрито для обраного періоду', 'success', {
                                                                    sx: {backgroundColor: '#1de416', color: 'white'}
                                                                });
                                                                setTimeout(() => window.location.reload(), 1200);
                                                            } else {
                                                                throw new Error(`HTTP ${res?.status}`);
                                                            }
                                                        } else {
                                                            const res = await api.patch(`/api/rates/pause/pause/${rateId}/`, {}, {
                                                                params: {
                                                                    pause_period_start: start,
                                                                    pause_period_end: end,
                                                                },
                                                            });

                                                            if (res && res.status === 200) {
                                                                const selectedEls = document.querySelectorAll('.selected');
                                                                selectedEls.forEach((el) => {
                                                                    el.classList.add('paused-price');
                                                                    el.setAttribute('data-pause-start', start);
                                                                    el.setAttribute('data-pause-end', end);
                                                                });
                                                                showSnackbar('Тариф поставлено на паузу для обраного періоду', 'success', {
                                                                    sx: {backgroundColor: '#1de416', color: 'white'}
                                                                });
                                                                setTimeout(() => window.location.reload(), 1200);
                                                            } else {
                                                                throw new Error(`HTTP ${res?.status}`);
                                                            }
                                                        }
                                                    } catch (e) {
                                                        showSnackbar(`Помилка при зміні статусу тарифу: ${e}`, 'error', {
                                                            sx: {backgroundColor: '#ff0000', color: 'white'}
                                                        });
                                                    }
                                                }}
                                                sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                            >
                                                <DoDisturbIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : null}
                            </Stack>
                        </Paper>
                    </Popper>

                    <Dialog open={confirmOverOpen} onClose={() => setConfirmOverOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Підтвердження</DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="body2">
                                Ви збираєтеся перенести бронювання в категорію «овербукінг». Ви впевнені?
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <MuiButton onClick={() => setConfirmOverOpen(false)}>Ні</MuiButton>
                            <MuiButton
                                variant="contained"
                                onClick={async () => {
                                    try {
                                        const res = await api.patch(
                                            `/api/booking_modules/use/booking/update-status/${clickedBookingId}/6/4/`,
                                            {status: 6, statusBook: 4}
                                        );
                                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                        setConfirmOverOpen(false);
                                        setAnchorEl(null);
                                        showSnackbar('Бронювання перенесено в овербукінг', 'success', {
                                            sx: {
                                                backgroundColor: '#e4bb16',
                                                color: 'white'
                                            }
                                        });
                                        setTimeout(() => {
                                            window.location.reload();
                                        }, 2000);
                                    } catch (e) {
                                        showSnackbar(`Не вдалося оновити статус бронювання. ${e}`, 'error', {
                                            sx: {
                                                backgroundColor: '#ff0000',
                                                color: 'white'
                                            }
                                        });
                                    }
                                }}
                            >
                                Так
                            </MuiButton>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={confirmBackOpen}
                        onClose={() => {
                            setConfirmBackOpen(false);
                            setTargetOverId(null);
                        }}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>Підтвердження</DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="body2">
                                Ви збираєтеся повернути бронювання з «овербукінгу» до звичайного статусу. Підтвердити?
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <MuiButton onClick={() => {
                                setConfirmBackOpen(false);
                                setTargetOverId(null);
                            }}>
                                Ні
                            </MuiButton>
                            <MuiButton variant="contained" onClick={doRemoveOverbooking}>Так</MuiButton>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={overListOpen} onClose={() => setOverListOpen(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Овербукінг — {overListItems.length} шт</DialogTitle>
                        <DialogContent dividers>
                            {overListItems.length === 0 ? (
                                <Typography variant="body2">Немає записів</Typography>
                            ) : (
                                <Stack spacing={1.2}>
                                    {overListItems.map((b) => (
                                        <Paper key={b.id} variant="outlined" sx={{p: 1}}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between"
                                                   gap={1}>
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        {`${(b.first_name || '')} ${(b.last_name || '')}`.trim() || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {`${format(startOfDay(parseDateTime(b.booking_from)), 'dd.MM.yyyy')} — ${format(endOfDay(parseDateTime(b.booking_to)), 'dd.MM.yyyy')}`}
                                                    </Typography>
                                                </div>
                                                <Stack direction="row" gap={1}>
                                                    <MuiButton size="small" onClick={() => openBookingDetails(b.id)}
                                                               startIcon={<VisibilityOutlinedIcon/>}>
                                                        Деталі
                                                    </MuiButton>
                                                    <MuiButton size="small" color="warning"
                                                               onClick={() => handleRemoveOverbooking(b.id)}
                                                               startIcon={<DoDisturbIcon/>}>
                                                        Прибрати
                                                    </MuiButton>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <MuiButton onClick={() => setOverListOpen(false)}>Закрити</MuiButton>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Редагування бронювання</DialogTitle>
                        <DialogContent dividers>
                            {editLoading ? (
                                <Typography variant="body2">Завантаження форми…</Typography>
                            ) : editForm && editValues ? (
                                <Stack spacing={1.2}>
                                    {editForm.booking_from && (
                                        <TextField
                                            label={editForm.booking_from.label || 'Дата початку бронювання'}
                                            type="datetime-local"
                                            value={editValues.booking_from}
                                            onChange={(e) => handleEditChange('booking_from', e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{shrink: true}}
                                        />
                                    )}
                                    {editForm.booking_to && (
                                        <TextField
                                            label={editForm.booking_to.label || 'Дата кінця бронювання'}
                                            type="datetime-local"
                                            value={editValues.booking_to}
                                            onChange={(e) => handleEditChange('booking_to', e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{shrink: true}}
                                        />
                                    )}
                                    {editForm.first_name && (
                                        <TextField
                                            label={editForm.first_name.label || 'Імʼя'}
                                            value={editValues.first_name}
                                            onChange={(e) => handleEditChange('first_name', e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                    {editForm.last_name && (
                                        <TextField
                                            label={editForm.last_name.label || 'Прізвище'}
                                            value={editValues.last_name}
                                            onChange={(e) => handleEditChange('last_name', e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                    {editForm.email && (
                                        <TextField
                                            label={editForm.email.label || 'Пошта'}
                                            value={editValues.email}
                                            onChange={(e) => handleEditChange('email', e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                    {editForm.phone && (
                                        <TextField
                                            label={editForm.phone.label || 'Телефон'}
                                            value={editValues.phone}
                                            onChange={(e) => handleEditChange('phone', e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                    {editForm.comments && (
                                        <TextField
                                            label={editForm.comments.label || 'Коментар'}
                                            value={editValues.comments}
                                            onChange={(e) => handleEditChange('comments', e.target.value)}
                                            size="small"
                                            fullWidth
                                            multiline
                                            minRows={3}
                                        />
                                    )}
                                    {editForm.time_arrived && (
                                        <TextField
                                            select
                                            label={editForm.time_arrived.label || 'Час прибуття'}
                                            value={editValues.time_arrived}
                                            onChange={(e) => handleEditChange('time_arrived', e.target.value)}
                                            size="small"
                                            fullWidth
                                        >
                                            {(editForm.time_arrived.choices || []).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2">Немає даних форми</Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <MuiButton onClick={() => setEditOpen(false)}>Скасувати</MuiButton>
                            <MuiButton variant="contained" onClick={handleEditSubmit} disabled={!editValues}>
                                Зберегти
                            </MuiButton>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={overListOpen} onClose={() => setOverListOpen(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Овербукінги цього періоду</DialogTitle>
                        <DialogContent dividers>
                            {overListItems && overListItems.length > 0 ? (
                                <Stack spacing={1.2}>
                                    {overListItems.map((b) => (
                                        <Paper
                                            key={b.id}
                                            sx={{
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                            variant="outlined"
                                        >
                                            <Typography variant="body2" sx={{mr: 1}}>
                                                {`#${b.id} — ${(b.first_name || '')} ${(b.last_name || '')}`.trim()}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Переглянути деталі" placement='top' arrow>
                                                    <IconButton size="small" onClick={() => openBookingDetails(b.id)}>
                                                        <VisibilityOutlinedIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Прибрати з овербукінгу" placement='top' arrow>
                                                    <IconButton size="small"
                                                                onClick={() => handleRemoveOverbooking(b.id)}>
                                                        <UndoOutlinedIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2">Немає записів</Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <MuiButton onClick={() => setOverListOpen(false)}>Закрити</MuiButton>
                        </DialogActions>
                    </Dialog>
                </div>

                <Dialog
                    open={detailsOpen}
                    onClose={() => {
                        setDetailsOpen(false);
                        setClickedBookingId(null);
                    }}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>Деталі бронювання</DialogTitle>
                    <DialogContent dividers>
                        {detailsLoading ? (
                            <Typography variant="body2">Завантаження…</Typography>
                        ) : bookingDetails ? (
                            <Stack spacing={1.2}>
                                <Typography variant="body2"><strong>ID:</strong> {bookingDetails.id}</Typography>
                                <Divider/>
                                <Typography variant="body2">
                                    <strong>Від:</strong> {bookingDetails.booking_from ? format(new Date(bookingDetails.booking_from), 'yyyy-MM-dd HH:mm') : '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>До:</strong> {bookingDetails.booking_to ? format(new Date(bookingDetails.booking_to), 'yyyy-MM-dd HH:mm') : '—'}
                                </Typography>
                                <Divider/>
                                <Typography variant="body2"><strong>Імʼя:</strong> {bookingDetails.first_name}
                                </Typography>
                                <Typography variant="body2"><strong>Прізвище:</strong> {bookingDetails.last_name}
                                </Typography>
                                <Typography variant="body2"><strong>Email:</strong> {bookingDetails.email}</Typography>
                                <Typography variant="body2"><strong>Телефон:</strong> {bookingDetails.phone}
                                </Typography>
                                <Typography variant="body2"><strong>Коментар:</strong> {bookingDetails.comments || '—'}
                                </Typography>
                                <Typography variant="body2"><strong>Час прибуття:</strong> {bookingDetails.time_arrived}
                                </Typography>
                                <Typography variant="body2"><strong>Ціна:</strong> {bookingDetails.price}</Typography>
                            </Stack>
                        ) : (
                            <Typography variant="body2">Немає даних для відображення</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MuiButton onClick={() => {
                            setDetailsOpen(false);
                            setClickedBookingId(null);
                        }}>
                            Закрити
                        </MuiButton>
                    </DialogActions>
                </Dialog>

                <Dialog open={closeModalOpen} onClose={() => setCloseModalOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Закрити період</DialogTitle>
                    <DialogContent dividers>
                        {closeFormValues ? (
                            <Stack spacing={1.2}>
                                <TextField
                                    label="Початок"
                                    type="date"
                                    value={closeFormValues.booking_from}
                                    onChange={(e) =>
                                        setCloseFormValues(v => ({...v, booking_from: e.target.value}))
                                    }
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{shrink: true}}
                                />

                                <TextField
                                    label="Кінець"
                                    type="date"
                                    value={closeFormValues.booking_to}
                                    onChange={(e) =>
                                        setCloseFormValues(v => ({...v, booking_to: e.target.value}))
                                    }
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{shrink: true}}
                                />

                                <TextField
                                    label="Імʼя"
                                    value={closeFormValues.first_name}
                                    size="small"
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                                <TextField
                                    label="Прізвище"
                                    value={closeFormValues.last_name}
                                    size="small"
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                                <TextField
                                    label="Email"
                                    value={closeFormValues.email}
                                    size="small"
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                                <TextField
                                    label="Телефон"
                                    value={closeFormValues.phone}
                                    size="small"
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                                <TextField
                                    label="Час прибуття"
                                    value={closeFormValues.time_arrived}
                                    size="small"
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                            </Stack>
                        ) : (
                            <Typography variant="body2">Немає даних</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MuiButton onClick={() => setCloseModalOpen(false)}>Скасувати</MuiButton>
                        <MuiButton variant="contained" onClick={submitCloseForm} disabled={!closeFormValues}>
                            Підтвердити
                        </MuiButton>
                    </DialogActions>
                </Dialog>
            </div>

            <div
                className={`${
                    (fetcher.state === 'loading' || fetcher.state === 'submitting') ? 'loading' : ''
                } mobile-version`}
                style={{display: 'none'}}
            >
                {fetcher.data?.error && (
                    <div style={{color: 'red', textAlign: 'center'}}>
                        {fetcher.data.error}
                    </div>
                )}

                <div className="table-container" style={{width: '100%', overflow: 'auto'}}>
                    <table className="mobile table-no_select" style={{width: '100%', tableLayout: 'fixed'}}>
                        <thead>
                        {renderTableHeader()}
                        </thead>
                        <tbody>
                        <RenderTableRows
                            data={data}
                            allDates={allDates}
                            onBookingClick={handleBookedCellClick}
                            setAnchorEl={setAnchorEl}
                            setPopupMode={setPopupMode}
                            setClickedBookingId={setClickedBookingId}
                            setOverListItems={setOverListItems}
                            setOverListOpen={setOverListOpen}
                        />
                        </tbody>
                    </table>
                </div>

                <div className="actions" style={{display: 'flex', justifyContent: 'space-around', marginTop: '20px'}}>
                    <MuiButton variant="contained" onClick={openClosePeriodModal}>Закрити</MuiButton>
                    <MuiButton variant="contained" onClick={() => navigate('book')}>
                        Забронювати
                    </MuiButton>
                </div>
            </div>

            <style jsx>{`
                .desktop-version {
                    display: none;
                }

                .mobile-version {
                    display: block;
                }

                @media (min-width: 768px) {
                    .desktop-version {
                        display: block;
                    }

                    .mobile-version {
                        display: none;
                    }
                }

                .table-no_select {
                    border-collapse: separate;
                    border-spacing: 0;
                }

                .table-no_select td, .table-no_select th {
                    height: 40px;
                    padding: 0;
                    vertical-align: middle;
                }

                .table-no_select td.object_title,
                .table-no_select th.object_title {
                    width: 180px;
                    min-width: 180px;
                    max-width: 180px;
                    padding: 0 8px;
                    box-sizing: border-box;
                }

                .table-no_select.desktop td:not(.object_title),
                .table-no_select.desktop th:not(.object_title) {
                    width: 48px;
                    min-width: 48px;
                    max-width: 48px;
                    text-align: center;
                }

                .calendar-chip {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 40px;
                    padding: 0 6px;
                    box-sizing: border-box;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .calendar-chip span {
                    font-size: 11px;
                    line-height: 1;
                }
            `}</style>
        </div>
    );
};

export default CalendarTableSingle;

export function flattenObjects(data) {
    const result = [];

    data.forEach(parent => {
        result.push(parent);

        if (parent.children_obj) {
            result.push(...parent.children_obj);
        }
    });

    return result;
}